import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Send,
  Copy,
  ChevronDown,
  Check,
  Trash2,
  Share2,
  X,
  Download,
  Clock,
  AlertCircle,
  Search,
  BookOpen,
  Compass,
  HelpCircle,
  ChevronUp,
  Sparkles,
  User,
  Star,
  Minus,
  Sun,
  Moon
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { getApiBase } from "../apiBase";
import { startStudentInboxPoll, isHumanQueuedChatResponse } from "../liveStudentInboxPoll";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const STAGE_TEXT = {
  typing: "Crafting your response",
  waiting3: "Gathering information for you",
  waiting5: "Almost ready — polishing details",
  waiting8: "Taking extra time to ensure quality",
};

const GeneratingText = ({ stage }) => {
  const [visible, setVisible] = useState("");
  const [cursor, setCursor] = useState(true);
  const text = STAGE_TEXT[stage] || STAGE_TEXT.typing;

  useEffect(() => {
    let i = 0;
    const typing = setInterval(() => {
      setVisible(text.slice(0, i + 1));
      i = (i + 1) % (text.length + 1);
    }, 80);
    const blink = setInterval(() => setCursor((c) => !c), 530);
    return () => {
      clearInterval(typing);
      clearInterval(blink);
    };
  }, [text]);

  return (
    <div className="flex items-center gap-1.5 text-xs font-medium text-[#a1a1aa]">
      <motion.div
        animate={{ rotate: [0, 15, -15, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <Sparkles size={12} className="text-[#ec4899]" />
      </motion.div>
      <span>{visible}</span>
      <motion.span
        animate={{ opacity: cursor ? 1 : 0 }}
        className="text-[#ec4899] font-mono text-xs"
      >
        ▋
      </motion.span>
    </div>
  );
};

const MessageBubble = ({
  message,
  index,
  isUser,
  onFeedback,
  feedbackShown,
  conversationEnded,
  onCopy,
  isExpanded,
  onToggleExpand,
  copiedIndex,
  isDarkMode
}) => {
  const [showTimestamp, setShowTimestamp] = useState(false);
  const isLong = message.text?.length > 300;

  const variants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 300, damping: 25, delay: index * 0.05 },
    },
  };

  return (
    <motion.div
      variants={variants}
      initial="hidden"
      animate="visible"
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4 group`}
    >
      <div
        className={`flex items-end gap-2.5 max-w-[85%] ${
          isUser ? "ml-auto flex-row" : "mr-auto flex-row"
        }`}
      >
        {!isUser && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring" }}
            className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#ec4899] to-[#a855f7] flex items-center justify-center shadow-lg flex-shrink-0"
          >
            <Sparkles size={10} className="text-white" />
          </motion.div>
        )}
        <div
          className={`relative px-4 py-3 rounded-2xl border transition-all duration-300 backdrop-blur-xl ${
            isUser
              ? "bg-gradient-to-br from-[#ec4899]/70 to-[#a855f7]/70 text-white rounded-br-md border-white/10 backdrop-blur-xl shadow-[0_8px_30px_rgba(236,72,153,0.35)]"
              : isDarkMode
                ? "bg-[#111111]/90 text-white rounded-bl-md border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.5)]"
                : "bg-gray-50 text-gray-800 rounded-bl-md border-gray-200 shadow-sm"
          }`}
          onMouseEnter={() => setShowTimestamp(true)}
          onMouseLeave={() => setShowTimestamp(false)}
          style={message.estimatedHeight ? { minHeight: `${message.estimatedHeight}px` } : {}}
        >
          {!isUser && isDarkMode && (
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-40 pointer-events-none" />
          )}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => <h1 className={`font-bold text-lg mt-3 mb-2 ${isDarkMode ? 'text-inherit' : 'text-gray-900'}`}>{children}</h1>,
              h2: ({ children }) => <h2 className={`font-semibold text-base mt-3 mb-2 ${isDarkMode ? 'text-inherit' : 'text-gray-900'}`}>{children}</h2>,
              p: ({ children }) => <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-inherit' : 'text-gray-700'}`}>{children}</p>,
              strong: ({ children }) => <span className="font-bold text-inherit">{children}</span>,
              ul: ({ children }) => <ul className={`list-disc ml-5 space-y-1 mt-2 ${isDarkMode ? 'text-inherit' : 'text-gray-700'}`}>{children}</ul>,
              ol: ({ children }) => <ol className={`list-decimal ml-5 space-y-1 mt-2 ${isDarkMode ? 'text-inherit' : 'text-gray-700'}`}>{children}</ol>,
              li: ({ children }) => <li className={`text-xs leading-relaxed ${isDarkMode ? 'text-inherit' : 'text-gray-700'}`}>{children}</li>,
              a: ({ href, children }) => (
                <a href={href} className="text-[#ec4899] hover:text-[#a855f7] underline transition-colors" target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
            }}
          >
            {isLong && !isExpanded ? `${message.text?.slice(0, 300)}...` : message.text}
          </ReactMarkdown>
          {isLong && (
            <button
              onClick={onToggleExpand}
              className={`mt-2 text-xs ${isDarkMode ? 'text-[#a1a1aa] hover:text-[#ec4899]' : 'text-gray-500 hover:text-[#ec4899]'} font-medium flex items-center gap-1`}
            >
              {isExpanded ? <><ChevronUp size={12} /> Show less</> : <><ChevronDown size={12} /> Read more</>}
            </button>
          )}
          {message.isTyping && (
            <div className="flex items-center gap-1 mt-2">
              {[0, 1, 2].map((dot) => (
                <motion.div
                  key={dot}
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 0.6, repeat: Infinity, delay: dot * 0.15 }}
                  className={`w-1.5 h-1.5 rounded-full ${isUser ? "bg-pink-300" : "bg-[#ec4899]"}`}
                />
              ))}
            </div>
          )}
          <AnimatePresence>
            {showTimestamp && !message.isTyping && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={`absolute -bottom-5 right-2 text-[10px] ${isDarkMode ? 'text-[#71717a]' : 'text-gray-400'} flex items-center gap-1`}
              >
                <Clock size={10} />{" "}
                {message.time?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </motion.div>
            )}
          </AnimatePresence>
          {!isUser && !message.isTyping && !message.isTypingPlaceholder && !conversationEnded && (
            <div className="flex items-center gap-1 mt-1">
              {feedbackShown ? (
                <span className={`text-xs ${isDarkMode ? 'text-[#ffffff]' : 'text-gray-800'} font-medium flex items-center gap-1`}>
                  <Check className="text-green-400" size={12} /> Feedback received
                </span>
              ) : !message.feedback ? (
                <>
                  <button onClick={() => onFeedback(index, "up")} className="text-[10px] px-1">👍</button>
                  <button onClick={() => onFeedback(index, "down")} className="text-[10px] px-1">👎</button>
                </>
              ) : null}
            </div>
          )}
        </div>
        {isUser && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring" }}
            className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#ec4899] to-[#a855f7] flex items-center justify-center shadow-lg flex-shrink-0"
          >
            <User size={12} className="text-white" />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

const WELCOME_MESSAGES = [
  "Hello! 👋 I'm your Droog Assist companion",
  "Ready to help you with anything today",
  "Ask me questions, get instant answers ✨",
];

const WelcomeScreen = ({ onStart, companyName, logoUrl, isDarkMode }) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [logoLoaded, setLogoLoaded] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessage((prev) => (prev + 1) % WELCOME_MESSAGES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (logoUrl) {
      const img = new Image();
      img.onload = () => setLogoLoaded(true);
      img.src = logoUrl;
    }
  }, [logoUrl]);

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${
      isDarkMode ? 'bg-black' : 'bg-gray-50'
    }`}>
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, type: "spring" }}
        className={`${
          isDarkMode 
            ? 'bg-[#111111] border-[#222222]' 
            : 'bg-white border-gray-200'
        } rounded-3xl p-8 sm:p-12 shadow-2xl border max-w-lg w-full text-center`}
      >
        {logoLoaded && logoUrl ? (
          <motion.img
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
            src={logoUrl}
            alt="Company Logo"
            className="w-20 h-20 mx-auto mb-6 object-contain"
          />
        ) : (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
            className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#ec4899] to-[#a855f7] flex items-center justify-center shadow-lg"
          >
            <Sparkles className="text-white text-3xl" />
          </motion.div>
        )}
        <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${
          isDarkMode ? 'text-[#ffffff]' : 'text-gray-900'
        }`}>
          {companyName ? `${companyName} Assist` : "Droog Assist"}
        </h1>
        <p className={`mb-6 text-sm ${
          isDarkMode ? 'text-[#a1a1aa]' : 'text-gray-600'
        }`}>
          Your intelligent companion for instant answers
        </p>
        <div className="h-12 mb-8 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentMessage}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`${isDarkMode ? 'text-[#ffffff]' : 'text-gray-800'} font-medium`}
            >
              {WELCOME_MESSAGES[currentMessage]}
            </motion.p>
          </AnimatePresence>
        </div>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onStart("")}
          className="w-full py-4 px-6 rounded-2xl font-semibold text-white shadow-lg bg-gradient-to-r from-[#ec4899] to-[#a855f7] hover:from-[#db2777] hover:to-[#9333ea] transition-all flex items-center justify-center gap-2"
        >
          <Send className="text-lg" /> Start Conversation
        </motion.button>
        <p className={`text-xs mt-6 ${isDarkMode ? 'text-[#71717a]' : 'text-gray-500'}`}>Powered by Droog • Secure & Private</p>
      </motion.div>
    </div>
  );
};

export default function EmbedChatUI() {
  const { tenantId, botId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [totalDuration, setTotalDuration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [branding, setBranding] = useState(null);
  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [conversationCount, setConversationCount] = useState(0);
  const [showScroll, setShowScroll] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [toast, setToast] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formTriggered, setFormTriggered] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [userHasDetails, setUserHasDetails] = useState(false); 
  const [phoneError, setPhoneError] = useState("");
  const [showConversationRating, setShowConversationRating] = useState(false);
  const [conversationRating, setConversationRating] = useState(0);
  const [conversationEnded, setConversationEnded] = useState(false);
  const [botIsTyping, setBotIsTyping] = useState(false);
  const [typingStage, setTypingStage] = useState("typing");
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isReady, setIsReady] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('droog_theme');
    return saved ? saved === 'dark' : true;
  });

  const chatEndRef = useRef(null);
  const chatBoxRef = useRef(null);
  const typingTimersRef = useRef([]);
  const textareaRef = useRef(null);
  const clientTimerAbandonSentRef = useRef(false);
  const inboxPollStopRef = useRef(null);
  const radius = 11;
  const circumference = 2 * Math.PI * radius;
  const progress = totalDuration ? timeLeft / totalDuration : 0;

  useEffect(() => {
    localStorage.setItem('droog_theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const formatUUID = (id) => {
    if (!id) return id;
    if (id.includes("-")) return id;
    return `${id.slice(0, 8)}-${id.slice(8, 12)}-${id.slice(12, 16)}-${id.slice(16, 20)}-${id.slice(20)}`;
  };

  const getBrowserFingerprint = async () => {
    const canonical = localStorage.getItem('droog_canonical_user_id');
    if (canonical) return canonical;
    const storedFingerprint = localStorage.getItem('droog_user_fingerprint');
    if (storedFingerprint) {
      return storedFingerprint;
    }
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const fingerprint = formatUUID(result.visitorId);
    localStorage.setItem('droog_user_fingerprint', fingerprint);
    return fingerprint;
  };

  const validatePhone = (value) => {
    if (!value) return false;
    if (!value.startsWith('+')) return false;
    const parsed = parsePhoneNumberFromString(value);
    return parsed ? parsed.isValid() : false;
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    setFormData({ ...formData, phone: val });
    if (val && !validatePhone(val)) {
      setPhoneError("Please include country code (e.g., +1...)");
    } else {
      setPhoneError("");
    }
  };

  const isConversationEnd = (text) => {
    const endWords = ["bye", "thank you", "thanks", "thankyou", "exit", "finished", "done", "that's all"];
    return endWords.some((word) => text.toLowerCase().includes(word));
  };

  const getClosingMessage = () => "✨ Thank you for chatting! I hope I helped make your day better. Come back anytime!";

  const clearTypingTimers = () => {
    typingTimersRef.current.forEach(clearTimeout);
    typingTimersRef.current = [];
  };

  const stopInboxPoll = useCallback(() => {
    if (inboxPollStopRef.current) {
      inboxPollStopRef.current();
      inboxPollStopRef.current = null;
    }
  }, []);

  const ensureInboxPoll = useCallback(() => {
    if (!sessionId) return;
    if (inboxPollStopRef.current) {
      inboxPollStopRef.current();
      inboxPollStopRef.current = null;
    }
    inboxPollStopRef.current = startStudentInboxPoll({
      sessionId,
      onAgentMessage: (msg) => {
        clearTypingTimers();
        setBotIsTyping(false);
        setMessages((prev) => {
          if (msg.id && prev.some((m) => m.inboxId === msg.id)) return prev;
          return [
            ...prev.filter((m) => !m.isTypingPlaceholder),
            { role: "bot", text: msg.text, inboxId: msg.id, time: new Date() },
          ];
        });
      },
      onModeChange: (mode) => {
        if (mode !== "human") stopInboxPoll();
      },
    });
  }, [sessionId, stopInboxPoll]);

  useEffect(() => () => stopInboxPoll(), [stopInboxPoll]);
  useEffect(() => {
    stopInboxPoll();
  }, [sessionId, stopInboxPoll]);

  const resetChatState = useCallback(() => {
    setMessages([]);
    setConversationCount(0);
    setUserMessageCount(0);
    setShowForm(false);
    setFormTriggered(false);
    setFormData({ name: "", email: "", phone: "" });
    setPhoneError("");
    setShowWelcome(true);
    setExpandedMessages({});
    setQuery("");
    setBotIsTyping(false);
    setTypingStage("typing");
    clearTypingTimers();
    stopInboxPoll();
    if (sessionId) {
      localStorage.removeItem(`draft_${sessionId}`);
      localStorage.removeItem(`user_message_count_${sessionId}`);
      localStorage.removeItem(`conversation_count_${sessionId}`);
      localStorage.removeItem(`lead_form_completed_${sessionId}`);
      localStorage.removeItem(`lead_form_shown_${sessionId}`);
    }
  }, [sessionId, stopInboxPoll]);

  const signalSessionAbandonBeacon = useCallback(
    (sid) => {
      if (!sid) return;
      const payload = JSON.stringify({
        sessionId: sid,
        tenantId: tenantId || undefined,
        botId: botId || undefined,
      });
      try {
        navigator.sendBeacon(
          `${getApiBase()}/chat/session/abandon`,
          new Blob([payload], { type: "application/json" })
        );
      } catch (err) {
        console.error("[EmbedChatUI] abandon sendBeacon failed", err);
      }
    },
    [tenantId, botId]
  );

  const handleSessionExpired = useCallback(() => {
    localStorage.removeItem("session_id");
    localStorage.removeItem("session_expires_at");
    const sid = sessionId;
    if (sid) {
      localStorage.removeItem(`draft_${sid}`);
      localStorage.removeItem(`user_message_count_${sid}`);
      localStorage.removeItem(`conversation_count_${sid}`);
      localStorage.removeItem(`lead_form_completed_${sid}`);
      localStorage.removeItem(`lead_form_shown_${sid}`);
    }
    resetChatState();
    setSessionId(null);
    setSessionExpiry(null);
    setTimeLeft(null);
    setTotalDuration(null);
    setConversationEnded(false);
    setShowConversationRating(false);
    setConversationRating(0);
    setShowWelcome(true);
  }, [sessionId, resetChatState]);

  /** Explicit "End Session" — same as rating close: summarize + completed + clear in-memory state */
  const handleExplicitEndSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      await fetch(`${getApiBase()}/sessions/terminate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, tenant_id: tenantId }),
      });
    } catch (err) {
      console.error("End session failed", err);
    }
    handleSessionExpired();
  }, [sessionId, tenantId, handleSessionExpired]);

  const sendTrackingData = async (sid, leadData = {}) => {
    try {
      let fingerprint = formatUUID(userId);
      if (!fingerprint) {
        fingerprint = await getBrowserFingerprint();
        setUserId(fingerprint);
      }
      await fetch(`${getApiBase()}/chat/session/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: sid,
          tenantId,
          botId,
          endUserId: fingerprint,
          fullName: leadData.name || null,
          email: leadData.email || null,
          phone: leadData.phone || null,
          primaryChannel: "web",
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          screen: `${window.screen.width}x${window.screen.height}`,
          language: navigator.language,
        }),
      });
    } catch (error) {
      console.error("Tracking error", error);
    }
  };

  const fetchBranding = async () => {
    try {
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/branding`);
      if (!res.ok) {
        setBrandingLoaded(true);
        return;
      }
      const data = await res.json();
      setBranding(data);
      if (tenantId && botId) {
        const logoUrl = `${getApiBase()}/tenant/${tenantId}/bot/${botId}/logo?t=${Date.now()}`;
        setLogoUrl(logoUrl);
        const img = new Image();
        img.onload = () => setLogoLoaded(true);
        img.onerror = () => setLogoLoaded(false);
        img.src = logoUrl;
      }
    } catch (err) {
      console.error("Branding fetch failed", err);
    } finally {
      setBrandingLoaded(true);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const storedSession = localStorage.getItem("session_id");
        const storedExpiry = localStorage.getItem("session_expires_at");
        if (
          storedSession &&
          storedExpiry &&
          Number(storedExpiry) > Date.now() / 1000 &&
          document.visibilityState === "visible"
        ) {
          setSessionId(storedSession);
          setSessionExpiry(Number(storedExpiry));
          setShowWelcome(false);
        } else {
          const fingerprint = await getBrowserFingerprint();
          setUserId(fingerprint);
          const res = await fetch(`${getApiBase()}/sessions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tenant_id: tenantId, bot_id: botId, end_user_id: fingerprint }),
          });
          const data = await res.json();
          if (data.session_id) {
            setSessionId(data.session_id);
            localStorage.setItem("session_id", data.session_id);
            sendTrackingData(data.session_id);
            setShowWelcome(false);
            if (data.timeout_info?.timeout_minutes) {
              const now = Date.now() / 1000;
              const exp = now + data.timeout_info.timeout_minutes * 60;
              setSessionExpiry(exp);
              setTimeLeft(data.timeout_info.timeout_minutes * 60);
              setTotalDuration(data.timeout_info.timeout_minutes * 60);
              localStorage.setItem("session_expires_at", exp);
            }
            resetChatState();
          }
        }
      } catch (err) {
        console.error("Session init failed", err);
      } finally {
        setLoading(false);
        setIsReady(true);
      }
    };
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/config`);
        const data = await res.json();
        const channels = data?.configuration?.channels_enabled || {};
        setConfig({ ...data.configuration, leads_enabled: channels?.lead_capture === true });
      } catch (err) {
        console.error("Config fetch failed", err);
      }
    };
    init();
    fetchConfig();
    fetchBranding();
  }, [tenantId, botId]);

  useEffect(() => {
    if (!sessionExpiry) return;
    const interval = setInterval(() => {
      const now = Date.now() / 1000;
      const remaining = sessionExpiry - now;
      if (remaining <= 0) {
        setTimeLeft(0);
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionExpiry]);

  useEffect(() => {
    clientTimerAbandonSentRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    const checkSessionStatus = async () => {
      try {
        const res = await fetch(`${getApiBase()}/sessions/${sessionId}/status`);
        if (res.status === 440) {
          handleSessionExpired();
          return;
        }
        if (res.status === 404) {
          handleSessionExpired();
          return;
        }
        if (!res.ok) return;
        const data = await res.json();
        if (data.status === "reconnect_required") {
          handleSessionExpired();
          return;
        }
        if (data.timeout_info?.minutes_remaining !== undefined) {
          const remainingSeconds = data.timeout_info.minutes_remaining * 60;
          setTimeLeft(remainingSeconds);
          if (!totalDuration) setTotalDuration(remainingSeconds);
          const newExpiry = Date.now() / 1000 + remainingSeconds;
          setSessionExpiry(newExpiry);
          localStorage.setItem("session_expires_at", String(newExpiry));
        }
        if (data.live_mode === "human") {
          ensureInboxPoll();
        } else if (data.live_mode === "ai") {
          stopInboxPoll();
        }
      } catch (err) {
        console.error("[EmbedChatUI] Session status check failed", err);
      }
    };
    const interval = setInterval(checkSessionStatus, 5000);
    checkSessionStatus();
    return () => clearInterval(interval);
  }, [sessionId, handleSessionExpired, ensureInboxPoll, stopInboxPoll]);

  /** Local countdown ended — server cleanup + summary via abandon (polling may lag in background tabs). */
  useEffect(() => {
    if (!sessionId || timeLeft !== 0) return;
    if (clientTimerAbandonSentRef.current) return;
    clientTimerAbandonSentRef.current = true;
    fetch(`${getApiBase()}/chat/session/abandon`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, tenantId, botId }),
      keepalive: true,
    }).catch(() => {});
    queueMicrotask(() => handleSessionExpired());
  }, [sessionId, timeLeft, tenantId, botId, handleSessionExpired]);

  useEffect(() => {
    if (!sessionId) return;
    const completed = localStorage.getItem(`lead_form_completed_${sessionId}`);
    const wasShown = localStorage.getItem(`lead_form_shown_${sessionId}`);
    if (completed === "true") {
      setFormTriggered(true);
      setShowForm(false);
    } else if (wasShown === "true") {
      setFormTriggered(true);
      setShowForm(true);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      setFormData({ name: "", email: "", phone: "" });
      setPhoneError("");
      const saved = localStorage.getItem(`draft_${sessionId}`);
      if (saved) setQuery(saved);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      const timer = setTimeout(() => localStorage.setItem(`draft_${sessionId}`, query), 500);
      return () => clearTimeout(timer);
    }
  }, [query, sessionId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showForm]);

  useEffect(() => {
    const handleTabClose = () => {
      const sid = typeof localStorage !== "undefined" ? localStorage.getItem("session_id") : null;
      if (!sid) return;
      signalSessionAbandonBeacon(sid);
    };
    window.addEventListener("beforeunload", handleTabClose);
    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
    };
  }, [signalSessionAbandonBeacon]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 2000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  useEffect(() => {
    if (!sessionId) return;
    const savedMsg = localStorage.getItem(`user_message_count_${sessionId}`);
    const savedConv = localStorage.getItem(`conversation_count_${sessionId}`);
    if (savedMsg !== null) setUserMessageCount(Number(savedMsg));
    if (savedConv !== null) setConversationCount(Number(savedConv));
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    localStorage.setItem(`user_message_count_${sessionId}`, userMessageCount);
    localStorage.setItem(`conversation_count_${sessionId}`, conversationCount);
  }, [userMessageCount, conversationCount, sessionId]);

  useEffect(() => {
    if (!timeLeft) return;
    if (timeLeft <= 120 && !showTimeoutWarning) {
      setShowTimeoutWarning(true);
      setToast("⚠️ Session ending in 2 minutes. Download your chat if needed.");
    }
    if (timeLeft > 120 && showTimeoutWarning) {
      setShowTimeoutWarning(false);
    }
  }, [timeLeft, showTimeoutWarning]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [query]);

  const typeBotResponseInBubble = (fullText) => {
    const words = fullText.split(" ");
    let currentWords = [];
    let i = 0;
    setMessages((prev) => [...prev, { role: "bot", text: "", isTyping: true, time: new Date() }]);
    const interval = setInterval(() => {
      if (i < words.length) {
        currentWords.push(words[i]);
        i++;
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1].text = currentWords.join(" ");
          return updated;
        });
      } else {
        clearInterval(interval);
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1].isTyping = false;
          delete updated[updated.length - 1].estimatedHeight;
          return updated;
        });
        setBotIsTyping(false);
        setConversationCount((c) => c + 1);
      }
    }, 90);
  };

  const startNewSession = async () => {
    try {
      if (sessionId) {
        ["lead_form_shown", "lead_form_completed", "user_message_count", "conversation_count"].forEach((key) =>
          localStorage.removeItem(`${key}_${sessionId}`)
        );
      }
      const fingerprint = userId || await getBrowserFingerprint();
      const res = await fetch(`${getApiBase()}/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: tenantId, bot_id: botId, end_user_id: fingerprint }),
      });
      const data = await res.json();
      if (data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem("session_id", data.session_id);
        sendTrackingData(data.session_id);
        if (data.timeout_info?.timeout_minutes) {
          const now = Date.now() / 1000;
          const exp = now + data.timeout_info.timeout_minutes * 60;
          setSessionExpiry(exp);
          setTimeLeft(data.timeout_info.timeout_minutes * 60);
          setTotalDuration(data.timeout_info.timeout_minutes * 60);
          localStorage.setItem("session_expires_at", exp);
        }
      }
    } catch (err) {
      console.error("Failed to start new session", err);
    }
  };

  const resetAndGreet = async () => {
    resetChatState();
    setConversationEnded(false);
    setShowConversationRating(false);
    setConversationRating(0);
    clearTypingTimers();
    setBotIsTyping(false);
    await startNewSession();
    setTimeout(() => {
      setMessages([
        { role: "bot", text: "Hello! I'm your Education Consultancy Assistant. How can I help you today?", time: new Date() },
      ]);
      setShowWelcome(false);
    }, 100);
  };

  const sendSilentMessage = async (text) => {
    clearTypingTimers();
    setTypingStage("typing");
    setMessages((prev) => [...prev, { role: "bot", isTypingPlaceholder: true, time: new Date() }]);
    setBotIsTyping(true);
    typingTimersRef.current.push(
      setTimeout(() => setTypingStage("waiting3"), 5000),
      setTimeout(() => setTypingStage("waiting5"), 12000),
      setTimeout(() => setTypingStage("waiting8"), 20000)
    );
    try {
      const res = await fetch(`${getApiBase()}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          tenant_id: tenantId,
          bot_id: botId,
          query: text,
          config,
          end_user_id: formatUUID(userId) || await getBrowserFingerprint(),
        }),
      });
      const data = await res.json();
      if (isHumanQueuedChatResponse(data)) {
        clearTypingTimers();
        setMessages((prev) => prev.filter((m) => !m.isTypingPlaceholder));
        setBotIsTyping(false);
        ensureInboxPoll();
        return;
      }
      stopInboxPoll();
      const botText = data.answer || data.bot_response || "Hmm, something went wrong.";
      if (data.session_info?.minutes_remaining) {
        const now = Date.now() / 1000;
        const newExpiry = now + data.session_info.minutes_remaining * 60;
        setSessionExpiry(newExpiry);
        setTimeLeft(data.session_info.minutes_remaining * 60);
        localStorage.setItem("session_expires_at", newExpiry);
      }
      if (data.is_lead_captured === true) {
          setUserHasDetails(true);
          if (showForm) setShowForm(false);
      }
      clearTypingTimers();
      setMessages((prev) => prev.filter((m) => !m.isTypingPlaceholder));
      typeBotResponseInBubble(botText);
    } catch (error) {
      clearTypingTimers();
      setBotIsTyping(false);
      setMessages((prev) => [
        ...prev.filter((m) => !m.isTypingPlaceholder),
        { role: "bot", text: "Sorry, I encountered an error. Please try again.", time: new Date() },
      ]);
    }
  };

  const sendMessage = async (preset) => {
    if (showForm) return;
    const text = preset ?? query.trim();
    if (!text || !sessionId || loading || botIsTyping) return;
    if (!preset) setQuery("");
    setShowWelcome(false);
    if (isConversationEnd(text)) {
      clearTypingTimers();
      setBotIsTyping(false);
      setMessages((p) => [...p, { role: "user", text, time: new Date() }]);
      setUserMessageCount((c) => c + 1);
      setMessages((p) => [...p, { role: "bot", text: getClosingMessage(), time: new Date() }]);
      setConversationEnded(true);
      setShowConversationRating(true);
      setSessionExpiry(null);
      setTimeLeft(null);
      return;
    }
    setMessages((p) => [...p, { role: "user", text, time: new Date() }]);
    setUserMessageCount((c) => c + 1);
    if (config?.leads_enabled && !formTriggered && userMessageCount + 1 >= 3) {
        if (userHasDetails) {
            console.log("User details already captured, skipping form.");
        } else {
            setShowForm(true);
            setFormTriggered(true);
            localStorage.setItem(`lead_form_shown_${sessionId}`, "true");
            return;
        }
    }
    clearTypingTimers();
    setTypingStage("typing");
    setMessages((p) => [...p, { role: "bot", isTypingPlaceholder: true, time: new Date() }]);
    setBotIsTyping(true);
    typingTimersRef.current.push(
      setTimeout(() => setTypingStage("waiting3"), 5000),
      setTimeout(() => setTypingStage("waiting5"), 12000),
      setTimeout(() => setTypingStage("waiting8"), 20000)
    );
    try {
      const res = await fetch(`${getApiBase()}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          tenant_id: tenantId,
          bot_id: botId,
          query: text,
          config,
          end_user_id: formatUUID(userId) || await getBrowserFingerprint(),
        }),
      });
      const data = await res.json();
      if (isHumanQueuedChatResponse(data)) {
        clearTypingTimers();
        setMessages((p) => p.filter((m) => !m.isTypingPlaceholder));
        setBotIsTyping(false);
        ensureInboxPoll();
        return;
      }
      stopInboxPoll();
      if (data.session_info?.minutes_remaining) {
        const now = Date.now() / 1000;
        const newExpiry = now + data.session_info.minutes_remaining * 60;
        setSessionExpiry(newExpiry);
        setTimeLeft(data.session_info.minutes_remaining * 60);
        localStorage.setItem("session_expires_at", newExpiry);
      }
      if (data.is_lead_captured === true) {
          setUserHasDetails(true);
          if (showForm) setShowForm(false);
      }
      clearTypingTimers();
      setMessages((p) => p.filter((m) => !m.isTypingPlaceholder));
      const reply = data.answer || data.bot_response || "Hmm, something went wrong.";
      typeBotResponseInBubble(reply);
    } catch {
      clearTypingTimers();
      setBotIsTyping(false);
      setMessages((p) => [
        ...p.filter((m) => !m.isTypingPlaceholder),
        { role: "bot", text: "⚠️ Something went wrong. Please try again.", time: new Date() },
      ]);
    }
  };

  const handleFormSubmit = async () => {
    if (!validatePhone(formData.phone)) {
      setPhoneError("Valid phone with country code required");
      return;
    }
    try {
      const parsedPhone = parsePhoneNumberFromString(formData.phone);
      const normalizedPhone = parsedPhone ? parsedPhone.format("E.164") : formData.phone;
      
      const createRes = await fetch(`${getApiBase()}/user/records/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          tenant_id: tenantId, 
          bot_id: botId, 
          session_id: sessionId, 
          endUserId: formatUUID(userId),
          name: formData.name, 
          email: formData.email, 
          phone: normalizedPhone 
        }),
      }).then(r => r.json()).catch(() => ({}));
      const canonicalId = createRes?.canonical_end_user_id;
      if (canonicalId && canonicalId !== formatUUID(userId)) {
        localStorage.setItem('droog_canonical_user_id', canonicalId);
      }
      
      await fetch(`${getApiBase()}/chat/session/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          sessionId, 
          tenantId, 
          botId, 
          endUserId: formatUUID(userId),
          fullName: formData.name, 
          email: formData.email, 
          phone: normalizedPhone, 
          primaryChannel: "web" 
        }),
      });
      
      const lastUserMessage = messages.filter((m) => m.role === "user").pop()?.text || "";
      setMessages((p) => [...p, { role: "bot", text: "Thanks! I've saved your details — let me continue answering your question.", time: new Date() }]);
      setShowForm(false);
      setFormTriggered(true);
      localStorage.setItem(`lead_form_completed_${sessionId}`, "true");
      setToast("✅ Details saved! Continuing your conversation...");
      setTimeout(() => setToast(null), 2000);
      setTimeout(() => sendSilentMessage(lastUserMessage), 400);
    } catch (err) {
      console.error("Form submit error", err);
      setToast("❌ Failed to save details. Please try again.");
      setTimeout(() => setToast(null), 2000);
    }
  };

  const handleFeedback = async (index, type) => {
    const msg = messages[index];
    setMessages((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], feedback: type, feedbackShown: true };
      return updated;
    });
    setTimeout(() => {
      setMessages((prev) => {
        const updated = [...prev];
        if (updated[index]) updated[index].feedbackShown = false;
        return updated;
      });
    }, 5000);
    try {
      await fetch(`${getApiBase()}/chat/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: tenantId, session_id: sessionId, bot_id: botId, message: msg.text, feedback: type }),
      });
    } catch (err) {
      console.error("Feedback error", err);
    }
  };

  const submitRating = async () => {
    if (!conversationRating) return;
    const sid = sessionId;
    try {
      await fetch(`${getApiBase()}/chat/conversation-rating`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: tenantId, bot_id: botId, session_id: sid, rating: conversationRating }),
      });
    } catch (err) {
      console.error("Rating submit error", err);
    }
    if (sid) {
      try {
        await fetch(`${getApiBase()}/sessions/terminate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sid, tenant_id: tenantId }),
        });
      } catch (err) {
        console.error("Session terminate after rating failed", err);
      }
    }
    localStorage.removeItem("session_id");
    localStorage.removeItem("session_expires_at");
    resetChatState();
    setSessionId(null);
    setSessionExpiry(null);
    setTimeLeft(null);
    setConversationEnded(false);
    setShowConversationRating(false);
    setConversationRating(0);
    setShowWelcome(true);
  };

  const downloadConversationAsPDF = async () => {
    if (isGeneratingPDF) return;
    setIsGeneratingPDF(true);
    setToast("Generating PDF...");
    let pdfContent = null;
    try {
      pdfContent = document.createElement("div");
      pdfContent.style.position = "fixed";
      pdfContent.style.left = "-9999px";
      pdfContent.style.top = "-9999px";
      pdfContent.style.width = "800px";
      pdfContent.style.padding = "40px";
      pdfContent.style.backgroundColor = isDarkMode ? "#000000" : "#ffffff";
      pdfContent.style.fontFamily = "Arial, sans-serif";
      document.body.appendChild(pdfContent);
      const header = document.createElement("div");
      header.style.textAlign = "center";
      header.style.paddingBottom = "30px";
      header.style.borderBottom = "2px solid #ec4899";
      header.style.marginBottom = "30px";
      const title = document.createElement("h1");
      title.style.fontSize = "28px";
      title.style.fontWeight = "bold";
      title.style.color = isDarkMode ? "#ffffff" : "#000000";
      title.style.margin = "10px 0";
      title.textContent = "Droog Assist";
      header.appendChild(title);
      pdfContent.appendChild(header);
      messages.forEach((msg) => {
        const messageDiv = document.createElement("div");
        messageDiv.style.marginBottom = "25px";
        messageDiv.style.padding = "15px";
        messageDiv.style.borderRadius = "12px";
        messageDiv.style.backgroundColor = msg.role === "user" ? "#ec4899" : (isDarkMode ? "#111111" : "#f9fafb");
        messageDiv.style.color = msg.role === "user" ? "white" : (isDarkMode ? "#ffffff" : "#1f2937");
        const messageText = document.createElement("div");
        messageText.style.fontSize = "14px";
        messageText.style.lineHeight = "1.6";
        messageText.textContent = msg.text;
        messageDiv.appendChild(messageText);
        pdfContent.appendChild(messageDiv);
      });
      const canvas = await html2canvas(pdfContent, { scale: 2, useCORS: true, logging: false });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`droog-chat-${new Date().toISOString().slice(0, 10)}.pdf`);
      setToast("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      setToast("Failed to generate PDF");
    } finally {
      setIsGeneratingPDF(false);
      if (pdfContent && document.body.contains(pdfContent)) {
        document.body.removeChild(pdfContent);
      }
    }
  };

  const clearChat = () => {
    if (window.confirm("Are you sure you want to clear this conversation?")) {
      resetChatState();
      setMessages([]);
      setToast("Chat cleared");
    }
  };

  const copyAllMessages = () => {
    const chatText = messages.map((m) => `${m.role === "user" ? "You" : "Bot"}: ${m.text}`).join("\n");
    navigator.clipboard.writeText(chatText);
    setToast("Entire conversation copied!");
  };

  const shareConversation = async () => {
    const chatText = messages.map((m) => `${m.role === "user" ? "👤 You" : "🤖 Bot"} (${m.time?.toLocaleTimeString()}):\n${m.text}\n`).join("\n");
    if (navigator.share) {
      try {
        await navigator.share({ title: "My Chat with Droog Assist", text: chatText });
      } catch (err) {
        if (err.name !== "AbortError") {
          downloadConversationAsPDF();
        }
      }
    } else {
      downloadConversationAsPDF();
    }
  };

  const handleQuickReply = (text) => {
    sendMessage(text);
  };

  const toggleExpand = (index) => {
    setExpandedMessages((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const copyText = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setToast("Copied!");
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  const isInputDisabled = botIsTyping || conversationEnded || showForm || loading || showConversationRating || isGeneratingPDF;
  const isPhoneValid = validatePhone(formData.phone);
  const WELCOME_QUESTIONS = branding?.welcome_questions?.length > 0 ? branding.welcome_questions : [];
  const QUESTION_ICONS = [Search, BookOpen, Compass, HelpCircle];

  if (!isReady || !brandingLoaded) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
        <div className={`${isDarkMode ? 'text-[#a1a1aa]' : 'text-gray-500'} text-sm animate-pulse`}>Loading...</div>
      </div>
    );
  }

  if (showWelcome && !sessionId) {
    return (
      <WelcomeScreen
        onStart={async (q) => {
          await startNewSession();
          setShowWelcome(false);
          if (q) setTimeout(() => sendMessage(q), 800);
        }}
        companyName={branding?.company_name}
        logoUrl={logoLoaded ? logoUrl : null}
        isDarkMode={isDarkMode}
      />
    );
  }

  const filteredMessages = searchQuery ? messages.filter((m) => m.text?.toLowerCase().includes(searchQuery.toLowerCase())) : messages;

  return (
    <div className="relative min-h-screen flex flex-col bg-transparent">
      <div className="absolute inset-0 -z-10 overflow-hidden bg-transparent">
        <motion.div
          animate={{
            background: [
              "radial-gradient(circle at 20% 20%, rgba(236,72,153,0.35) 0%, transparent 50%)",
              "radial-gradient(circle at 80% 30%, rgba(168,85,247,0.35) 0%, transparent 50%)",
              "radial-gradient(circle at 50% 80%, rgba(99,102,241,0.35) 0%, transparent 50%)",
              "radial-gradient(circle at 20% 20%, rgba(236,72,153,0.35) 0%, transparent 50%)"
            ]
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute inset-0 blur-3xl opacity-40"
        />
      </div>

      <div className={`fixed top-0 left-0 right-0 z-30 backdrop-blur-xl border-b transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-gradient-to-r from-[#ec4899]/10 via-[#a855f7]/10 to-[#6366f1]/10 border-white/10' 
          : 'bg-gradient-to-r from-[#ec4899]/5 via-[#a855f7]/5 to-[#6366f1]/5 border-gray-200'
      }`}>
        <div className="max-w-3xl mx-auto px-4 py-3.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {logoLoaded && logoUrl ? (
                <img src={logoUrl} alt="Company Logo" className="w-9 h-9 object-contain rounded-lg" />
              ) : (
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#ec4899] to-[#a855f7] flex items-center justify-center"
                >
                  <Sparkles className="text-white text-lg" />
                </motion.div>
              )}
              <div>
                <h1 className={`font-bold text-sm sm:text-base ${isDarkMode ? 'text-[#ffffff]' : 'text-gray-900'}`}>
                  {branding?.company_name || "Droog Assist"}
                </h1>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <p className={`text-xs ${isDarkMode ? 'text-[#a1a1aa]' : 'text-gray-600'}`}>Online</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {timeLeft !== null && (
                <div className="relative w-7 h-7">
                  <svg className="w-7 h-7 rotate-[-90deg]">
                    <circle cx="14" cy="14" r="11" stroke={isDarkMode ? "#222222" : "#e5e7eb"} strokeWidth="3" fill="transparent" />
                    <motion.circle
                      cx="14"
                      cy="14"
                      r={radius}
                      stroke={timeLeft < 60 ? "#ef4444" : timeLeft < 120 ? "#f59e0b" : "#22c55e"}
                      strokeWidth="3"
                      fill="transparent"
                      strokeDasharray={circumference}
                      strokeDashoffset={circumference * (1 - progress)}
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'bg-[#222222] hover:bg-[#333333] text-[#fbbf24]' 
                    : 'bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#6366f1]'
                }`}
                title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className={`fixed top-[73px] left-0 right-0 z-20 px-4 py-2 border-t ${
              isDarkMode ? 'bg-[#1a1a1a] border-[#222222]' : 'bg-white border-gray-200'
            }`}
          >
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search messages..."
              className={`w-full px-3 py-2 rounded-lg text-sm ${
                isDarkMode 
                  ? 'bg-[#111111] border border-[#222222] text-[#ffffff] focus:ring-2 focus:ring-[#ec4899]' 
                  : 'bg-gray-50 border border-gray-300 text-gray-900 focus:ring-2 focus:ring-[#ec4899]'
              } outline-none`}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div
        ref={chatBoxRef}
        className={`relative flex-1 overflow-y-auto scroll-smooth hide-scrollbar border-y transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-black/90 border-white/10' 
            : 'bg-white/95 border-gray-200'
        }`}
        style={{ paddingTop: showSearch ? "130px" : "73px", paddingBottom: "100px" }}
      >
        {isDarkMode && (
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/5 via-transparent to-transparent opacity-30"></div>
        )}
        <div className="w-full max-w-3xl mx-auto px-4 py-6 space-y-4">
          {messages.length === 0 && !showForm && (
            <div className="h-full flex flex-col items-center justify-center text-center pt-12">
              {logoLoaded && logoUrl ? (
                <motion.img
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", damping: 15 }}
                  src={logoUrl}
                  alt="Company Logo"
                  className="w-20 h-20 mb-6 object-contain"
                />
              ) : (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", damping: 15 }}
                  className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#ec4899] to-[#a855f7] flex items-center justify-center mb-6 shadow-lg"
                >
                  <Sparkles className="text-5xl text-white" />
                </motion.div>
              )}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`text-3xl md:text-4xl font-bold mb-2 ${isDarkMode ? 'text-[#ffffff]' : 'text-gray-900'}`}
              >
                Welcome to {branding?.company_name || "Droog Assist"}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`${isDarkMode ? 'text-[#a1a1aa]' : 'text-gray-600'} max-w-md mb-8 text-lg`}
              >
                {branding?.welcome_subtitle || "Your AI-powered Assistant"}
              </motion.p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="w-full max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                {WELCOME_QUESTIONS.map((question, idx) => {
                  const Icon = QUESTION_ICONS[idx % QUESTION_ICONS.length];
                  return (
                    <motion.button
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + idx * 0.06 }}
                      whileHover={{ y: -2, boxShadow: "0 8px 20px rgba(236,72,153,0.3)" }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleQuickReply(question)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-left text-sm ${
                        isDarkMode 
                          ? 'bg-[#111111] border border-[#222222] text-[#ffffff] hover:bg-black hover:border-[#ec4899]' 
                          : 'bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 hover:border-[#ec4899]'
                      } transition-all shadow-sm`}
                    >
                      <div className={`w-6 h-6 flex items-center justify-center rounded-md ${
                        isDarkMode ? 'bg-[#111111] text-[#ec4899]' : 'bg-gray-100 text-[#ec4899]'
                      } shrink-0`}>
                        <Icon size={14} />
                      </div>
                      <span className="leading-tight">{question}</span>
                    </motion.button>
                  );
                })}
              </motion.div>
            </div>
          )}
          <AnimatePresence mode="popLayout">
            {filteredMessages.map((m, i) => {
              const isUser = m.role === "user";
              if (m.isTypingPlaceholder) {
                return (
                  <motion.div
                    key={`typing-${i}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex justify-start mb-4"
                  >
                    <div className="ml-0">
                      <GeneratingText stage={typingStage} />
                    </div>
                  </motion.div>
                );
              }
              return (
                <MessageBubble
                  key={i}
                  message={m}
                  index={i}
                  isUser={isUser}
                  onFeedback={handleFeedback}
                  feedbackShown={m.feedbackShown}
                  conversationEnded={conversationEnded}
                  onCopy={copyText}
                  isExpanded={expandedMessages[i]}
                  onToggleExpand={() => toggleExpand(i)}
                  copiedIndex={copiedIndex}
                  isDarkMode={isDarkMode}
                />
              );
            })}
          </AnimatePresence>

          {showForm && config?.leads_enabled && (
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className={`w-full p-5 sm:p-6 ${
                isDarkMode ? 'bg-[#111111] border border-[#222222]' : 'bg-white border border-gray-200'
              } rounded-2xl shadow-lg mt-2`}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#ec4899] to-[#a855f7] flex items-center justify-center flex-shrink-0 shadow-sm">
                  <User className="text-white text-lg" />
                </div>
                <div>
                  <p className={`text-sm font-semibold ${isDarkMode ? 'text-[#ffffff]' : 'text-gray-900'}`}>Almost there!</p>
                  <p className={`text-xs ${isDarkMode ? 'text-[#a1a1aa]' : 'text-gray-600'}`}>Share your details to continue the conversation</p>
                </div>
              </div>
              <div className="space-y-3.5">
                {["name", "email"].map((field) => (
                  <motion.input
                    key={field}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                    value={formData[field]}
                    onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                    className={`w-full px-4 py-3 rounded-xl border text-sm focus:ring-2 focus:ring-[#ec4899] outline-none transition-all ${
                      isDarkMode 
                        ? 'bg-black hover:border-[#ec4899] text-[#ffffff] border-[#222222]' 
                        : 'bg-white hover:border-[#ec4899] text-gray-900 border-gray-300'
                    }`}
                  />
                ))}
                <div>
                  <motion.input
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    className={`w-full px-4 py-3 rounded-xl border text-sm focus:ring-2 outline-none transition-all ${
                      isDarkMode ? 'bg-black hover:border-[#ec4899] text-[#ffffff]' : 'bg-white hover:border-[#ec4899] text-gray-900'
                    } ${
                      phoneError ? "border-red-400 focus:ring-red-400" : isDarkMode ? "border-[#222222] focus:ring-[#ec4899]" : "border-gray-300 focus:ring-[#ec4899]"
                    }`}
                  />
                  <AnimatePresence>
                    {phoneError && (
                      <motion.p
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="text-xs text-red-400 mt-1.5 flex items-center gap-1.5"
                      >
                        <X className="text-red-400" size={12} /> {phoneError}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleFormSubmit}
                  disabled={!isPhoneValid}
                  className={`w-full mt-2 py-3 rounded-xl font-semibold shadow-sm transition-all text-sm flex items-center justify-center gap-2 ${
                    !isPhoneValid
                      ? isDarkMode
                        ? "bg-[#222222] text-[#71717a] cursor-not-allowed"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-[#ec4899] to-[#a855f7] hover:from-[#db2777] hover:to-[#9333ea] text-white"
                  }`}
                >
                  <Check className="text-lg" /> Continue Conversation
                </motion.button>
              </div>
            </motion.div>
          )}

          {showTimeoutWarning && timeLeft && timeLeft <= 120 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`fixed top-20 left-1/2 -translate-x-1/2 ${
                isDarkMode ? 'bg-[#111111] border-l-4 border-yellow-500' : 'bg-white border-l-4 border-yellow-500'
              } p-4 rounded-xl shadow-lg z-40 max-w-md w-full mx-4`}
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="text-yellow-500 mt-1 flex-shrink-0 text-lg" />
                <div className="text-sm">
                  <p className={`font-semibold mb-1 ${isDarkMode ? 'text-[#ffffff]' : 'text-gray-900'}`}>Session Ending Soon</p>
                  <p className={isDarkMode ? 'text-[#a1a1aa]' : 'text-gray-600'}>
                    Your session will end in{" "}
                    <span className="font-bold text-yellow-400">
                      {Math.floor(timeLeft / 60)}m {Math.floor(timeLeft % 60)}s
                    </span>
                    .{" "}
                    <button onClick={downloadConversationAsPDF} className="ml-1 text-[#ec4899] font-bold hover:underline">
                      Download your chat
                    </button>{" "}
                    to save it.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
          <div ref={chatEndRef} />
        </div>
      </div>

      {showScroll && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          whileHover={{ scale: 1.1, backgroundColor: "#ec4899" }}
          whileTap={{ scale: 0.95 }}
          onClick={() => chatEndRef.current.scrollIntoView({ behavior: "smooth" })}
          className="fixed bottom-28 right-6 p-3 rounded-full bg-gradient-to-r from-[#ec4899] to-[#a855f7] text-white shadow-lg transition-colors"
        >
          <ChevronDown size={24} />
        </motion.button>
      )}

      <div className={`fixed bottom-0 left-0 right-0 z-30 backdrop-blur-xl border-t transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-gradient-to-r from-[#ec4899]/20 via-[#a855f7]/20 to-[#6366f1]/20 border-white/10' 
          : 'bg-gradient-to-r from-[#ec4899]/10 via-[#a855f7]/10 to-[#6366f1]/10 border-gray-200'
      }`}>
        <div className="max-w-3xl mx-auto px-4 py-4">
          <motion.div
            animate={{ y: showForm || conversationEnded ? 10 : 0 }}
            transition={{ type: "spring", stiffness: 300 }}
            className="flex items-center gap-3"
          >
            <div className="flex-1 relative">
  <textarea
    ref={textareaRef}
    value={query}
    disabled={isInputDisabled}
    onChange={(e) => setQuery(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!isInputDisabled) sendMessage();
      }
    }}
    placeholder={
      conversationEnded
        ? "Session ended"
        : showForm
        ? "Complete form first"
        : isGeneratingPDF
        ? "Generating PDF..."
        : "Type your message..."
    }
    rows={1}
    className={`w-full px-5 py-3 rounded-2xl min-h-[52px]
      border text-sm pr-14 resize-none overflow-hidden
      focus:ring-2 focus:ring-[#ec4899] focus:border-transparent outline-none transition-all
      ${
        isInputDisabled
          ? "bg-[#0a0a0a] text-[#71717a] border-[#222222] cursor-not-allowed"
          : "bg-[#0a0a0a] border-[#222222] text-white hover:border-[#ec4899] shadow-sm"
      }`}
  />

  <motion.button
  whileHover={{}}
  whileTap={{ scale: 1 }}
  type="button"
  onClick={() => sendMessage()}
  disabled={isInputDisabled || !query.trim()}
  className={`absolute right-3 inset-y-0 my-auto -translate-y-0.5
    w-8 h-8 rounded-full
    flex items-center justify-center
    border border-white/10
    transition-colors duration-300
    ${
      isInputDisabled || !query.trim()
        ? "bg-[#1a1a1a] text-[#71717a] cursor-not-allowed"
        : "bg-white/10 text-[#a1a1aa] hover:text-white hover:bg-white/20"
    }`}
>
  <Send size={17} strokeWidth={2.3} />
</motion.button>
</div>
          </motion.div>
          <div className="flex items-center justify-between mt-3">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className={`text-[10px] font-medium tracking-wide ${
              isDarkMode ? 'text-[#71717a]' : 'text-gray-500'
            }`}>
              Powered by Droog
            </motion.p>
            <div className="flex items-center gap-3 text-[10px] text-[#a1a1aa]">
              <button
                type="button"
                onClick={handleExplicitEndSession}
                className="text-[10px] text-[#ec4899] hover:underline"
              >
                End Session
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-pink-500/40 to-transparent" />
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 px-5 py-3.5 rounded-xl bg-gradient-to-r from-[#ec4899] to-[#a855f7] text-white text-sm font-medium shadow-lg z-[3000] flex items-center gap-2.5"
          >
            <Check className="text-green-300" /> {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showConversationRating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[2000] p-4"
            onClick={(e) => e.target === e.currentTarget && setShowConversationRating(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              className={`${
                isDarkMode ? 'bg-[#111111] border border-[#222222]' : 'bg-white border border-gray-200'
              } w-full max-w-md rounded-3xl shadow-lg p-7 text-center relative overflow-hidden`}
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/15 via-transparent to-transparent opacity-60 pointer-events-none" />
              <div className="relative z-10">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: "spring" }}
                  className={`w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-2xl ${
                    isDarkMode ? 'bg-[#111111]' : 'bg-gray-50'
                  } shadow-inner`}
                >
                  <Star className="text-3xl text-[#f59e0b]" />
                </motion.div>
                <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-[#ffffff]' : 'text-gray-900'}`}>Rate your experience</h2>
                <p className={`text-sm mb-6 ${isDarkMode ? 'text-[#a1a1aa]' : 'text-gray-600'}`}>Your feedback shapes the future of Droog Assist</p>
                <div className="flex justify-center gap-2 mb-7">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.button
                      key={star}
                      whileHover={{ scale: 1.3, y: -3 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setConversationRating(star)}
                      className={`text-4xl transition-all duration-200 ${
                        conversationRating >= star ? "text-[#f59e0b] drop-shadow-sm" : isDarkMode ? "text-[#222222] hover:text-[#f59e0b]" : "text-gray-300 hover:text-[#f59e0b]"
                      }`}
                    >
                      ★
                    </motion.button>
                  ))}
                </div>
                <AnimatePresence mode="wait">
                  {conversationRating > 0 && (
                    <motion.p
                      key={conversationRating}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className={`text-sm font-medium mb-5 ${isDarkMode ? 'text-[#ffffff]' : 'text-gray-800'}`}
                    >
                      {conversationRating === 5 && "🌟 Excellent! We're thrilled!"}
                      {conversationRating === 4 && "😊 Great! Thanks for the feedback"}
                      {conversationRating === 3 && "🙂 Good! We'll keep improving"}
                      {conversationRating === 2 && "😕 Thanks for helping us grow"}
                      {conversationRating === 1 && "😞 We're sorry — tell us how to improve"}
                    </motion.p>
                  )}
                </AnimatePresence>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={submitRating}
                  disabled={!conversationRating}
                  className={`w-full py-3.5 rounded-2xl font-semibold transition-all shadow-sm ${
                    conversationRating
                      ? "bg-gradient-to-r from-[#ec4899] to-[#a855f7] hover:from-[#db2777] hover:to-[#9333ea] text-white"
                      : isDarkMode
                        ? "bg-[#222222] text-[#71717a] cursor-not-allowed"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Submit Feedback
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}